import {
  BRAND_TARGET_SIZE_INCHES,
  BrandCategory,
  SMALL_BRAND_TARGET_SIZE_INCHES,
  LARGE_BRAND_TARGET_SIZE_INCHES,
} from "./brands";
import { getSideSourceBounds } from "./sideTextureMappings";

const BRAND_SIZE_OVERRIDES_INCHES = {
  [BrandCategory.ALPHANUMERIC_LARGE]: {
    // The Q's tail is included in its image bounds, which makes its bowl read
    // smaller than the other one-inch letter faces at the nominal scale.
    "letter-q": 1.08,
  },
  [BrandCategory.SHAPES_SYMBOLS]: {
    // Supplied physical branding-iron dimensions. All unlisted symbols are
    // one inch at their longest point.
    "circle-outline": 1.375,
    "diamond-outline": 1.625,
    "wave": 1.5,
    "bar": 1.25,
    "small-star": 0.75,
    "everwest-small": 0.875,
    "everwest-large": 2,
  },
};

const EXACT_BRAND_DIMENSIONS_INCHES = {
  // These two irons have supplied width and height measurements, so preserve
  // those dimensions independently instead of inferring height from the PNG.
  "everwest-small": { width: 0.875, height: 0.625 },
  "everwest-large": { width: 2, height: 1.24 },
};

const BRAND_PICKER_PREVIEW_SCALES = {
  // Keep the drawer preview proportional to the supplied physical iron sizes.
  // The other two stars are one inch across.
  "small-star": 0.75,
};

// These products were specifically recalibrated against their 2.5-inch patch
// (and the one-inch solid circle). Their letter and Greek artwork now uses the
// nominal physical height directly. Products not listed here retain their
// previously approved artwork correction, including both Dopp kits.
const NOMINAL_LETTER_MODEL_PREFIXES = [
  "Large_Weekender_",
  "Backpack_",
  "Zippered_",
];
const LETTER_ARTWORK_SCALE_CORRECTION = 0.6;

export const getBrandPickerPreviewScale = (assetName) =>
  BRAND_PICKER_PREVIEW_SCALES[assetName] ?? 1;

const getSideRealWorldWidthInches = (model, side) => {
  if (side === "top") {
    return model.realWorldTopWidthInches ?? model.realWorldWidthInches;
  }

  if (side === "left" || side === "right") {
    return model.realWorldDepthInches;
  }

  return model.realWorldWidthInches;
};

const getSideRealWorldHeightInches = (model, side) =>
  side === "top" ? model.realWorldDepthInches : model.realWorldHeightInches;

export const getBrandTargetSizeInches = (selectedCategory, assetName) => {
  const sizeOverride = BRAND_SIZE_OVERRIDES_INCHES[selectedCategory]?.[assetName];
  if (sizeOverride !== undefined) {
    return sizeOverride;
  }

  if (selectedCategory === BrandCategory.ALPHANUMERIC_SMALL) {
    return SMALL_BRAND_TARGET_SIZE_INCHES;
  } else if (assetName === "everwest-large") {
    return LARGE_BRAND_TARGET_SIZE_INCHES;
  }
  return BRAND_TARGET_SIZE_INCHES;
};

export const getBrandTargetDimensionsInches = (selectedCategory, assetName) =>
  selectedCategory === BrandCategory.SHAPES_SYMBOLS
    ? EXACT_BRAND_DIMENSIONS_INCHES[assetName] ?? null
    : null;

export const isHeightCalibratedBrand = (selectedCategory) =>
  selectedCategory === BrandCategory.ALPHANUMERIC_LARGE ||
  selectedCategory === BrandCategory.ALPHANUMERIC_SMALL ||
  selectedCategory === BrandCategory.GREEK_ALPHABET;

const usesNominalLetterCalibration = (model, side) =>
  model.name.startsWith("Keychain_") ||
  (model.name.startsWith("Small_Weekender_") && side === "top") ||
  NOMINAL_LETTER_MODEL_PREFIXES.some((prefix) => model.name.startsWith(prefix));

export const getBrandArtworkScaleCorrection = (model, selectedCategory, side) =>
  isHeightCalibratedBrand(selectedCategory) && !usesNominalLetterCalibration(model, side)
    ? LETTER_ARTWORK_SCALE_CORRECTION
    : 1;

// These corrections align the physical-inch ruler with product surfaces whose
// photographed/projected width differs from the full nominal product ruler.
// They adjust only a brand's initial size; placement coordinates and the
// established 2D-to-3D texture mappings remain untouched.
export const getBrandSurfaceScaleCorrection = (model, side, selectedCategory) => {
  if (
    model.name.startsWith("Backpack_") &&
    (side === "left" || side === "right")
  ) {
    return getSideSourceBounds(model, side).widthScale;
  }

  if (
    model.name.startsWith("Toiletry_") &&
    selectedCategory === BrandCategory.SHAPES_SYMBOLS
  ) {
    return LETTER_ARTWORK_SCALE_CORRECTION;
  }

  return 1;
};

const getPhysicalPixelSize = (model, side, sourceBounds) => {
  const horizontal = getSideRealWorldWidthInches(model, side) / sourceBounds.width;
  const vertical = sourceBounds.preserveDecalAspect
    ? horizontal
    : getSideRealWorldHeightInches(model, side) / sourceBounds.height;
  return { horizontal, vertical };
};

export const getBrandScalesForModel = ({
  imageHeight,
  imageWidth,
  model,
  selectedCategory,
  side,
  assetName,
}) => {
  const sourceBounds = getSideSourceBounds(model, side);
  const targetSize = getBrandTargetSizeInches(selectedCategory, assetName);

  if (isHeightCalibratedBrand(selectedCategory) && !usesNominalLetterCalibration(model, side)) {
    // Preserve the already-approved Dopp-kit and Small Weekender lettering.
    // Their artwork correction was calibrated using the projected leather
    // surface rather than the nominal product ruler.
    const surfaceWidth = getSideRealWorldWidthInches(model, side) * sourceBounds.widthScale;
    const surfaceHeight = getSideRealWorldHeightInches(model, side) * sourceBounds.heightScale;
    const unscaledHeight = sourceBounds.preserveDecalAspect
      ? (imageHeight / sourceBounds.width) * surfaceWidth
      : (imageHeight / sourceBounds.height) * surfaceHeight;
    const scale = unscaledHeight
      ? targetSize * getBrandArtworkScaleCorrection(model, selectedCategory, side) / unscaledHeight
      : 1;
    return { scaleX: scale, scaleY: scale };
  }

  const pixelSize = getPhysicalPixelSize(model, side, sourceBounds);
  const surfaceCorrection = getBrandSurfaceScaleCorrection(model, side, selectedCategory);
  if (isHeightCalibratedBrand(selectedCategory)) {
    const scale = imageHeight * pixelSize.vertical
      ? targetSize * surfaceCorrection / (imageHeight * pixelSize.vertical)
      : 1;
    return { scaleX: scale, scaleY: scale };
  }

  const exactDimensions = getBrandTargetDimensionsInches(selectedCategory, assetName);
  if (exactDimensions) {
    return {
      scaleX: exactDimensions.width * surfaceCorrection / (imageWidth * pixelSize.horizontal),
      scaleY: exactDimensions.height * surfaceCorrection / (imageHeight * pixelSize.vertical),
    };
  }

  const unscaledLongestPoint = Math.max(
    imageWidth * pixelSize.horizontal,
    imageHeight * pixelSize.vertical
  );
  const scale = unscaledLongestPoint
    ? targetSize * surfaceCorrection / unscaledLongestPoint
    : 1;
  return { scaleX: scale, scaleY: scale };
};

// Retain the scalar API for callers and tests that only need uniform brands.
export const getBrandScaleForModel = (options) =>
  getBrandScalesForModel(options).scaleX;
